import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Chess } from 'chess.js';
import { getChessPlayerProfile, rematchGame } from '../../api/playing';
import PageHeaderSection from '../../components/layout/PageHeaderSection';
import Button from '../../components/common/Button';
import { apiErrorMessage } from '../../utils/apiError';
import { GameListRow, PlayingBanner, PlayingEmptyState, PlayingSkeleton } from './playingUi';
import { lineForEco, lookupOpening } from '../../playing/openingBook';
import { localizeOpeningName } from '../../playing/openingLocalize';
import { getRoles, getUserId, getActiveRole } from '../../utils/auth';
import {
  ChartPanel,
  TrendLine,
  CompareBar,
  CompositionDonut,
  CHART_COLORS,
} from '../../components/charts';

const SPEED_ORDER = ['BULLET', 'BLITZ', 'RAPID', 'CLASSICAL', 'CORRESPONDENCE'];
const FINISHED = new Set(['WHITE_WON', 'BLACK_WON', 'DRAW']);

function exploreOpeningPath(eco) {
  const line = lineForEco(eco);
  if (!line?.ucis?.length) return '/playing/coach-board';
  try {
    const c = new Chess();
    line.ucis.forEach((uci) => {
      const u = String(uci).toLowerCase();
      c.move({
        from: u.slice(0, 2),
        to: u.slice(2, 4),
        promotion: u.length > 4 ? u[4] : undefined,
      });
    });
    const params = new URLSearchParams();
    params.set('pgn', c.pgn({ maxWidth: 0, newline: ' ' }));
    return `/playing/coach-board?${params.toString()}`;
  } catch {
    return '/playing/coach-board';
  }
}

function canRematchProfileGame(g) {
  const parent = String(getActiveRole() || '').toUpperCase() === 'PARENT'
    || (getRoles() || []).some((r) => String(r).toUpperCase().replace(/^ROLE_/, '') === 'PARENT');
  if (parent) return false;
  if (!g?.endedAt && !FINISHED.has(String(g?.status || '').toUpperCase())) return false;
  if (String(g?.gameType || '').toUpperCase() === 'VS_COMPUTER') return false;
  if (String(g?.status || '').toUpperCase() === 'ABORTED') return false;
  const me = Number(getUserId());
  if (!me) return false;
  return Number(g.whiteUserId) === me || Number(g.blackUserId) === me;
}

/** Own/staff games → play route; others → spectate (or null if private). */
function profileGameHref(g, { analyze = false } = {}) {
  if (!g?.id) return null;
  const me = Number(getUserId());
  const roles = (getRoles() || []).map((r) => String(r).toUpperCase().replace(/^ROLE_/, ''));
  const staff = roles.some((r) => r === 'ADMIN' || r === 'MANAGER' || r === 'COACH');
  const mine = me
    && (Number(g.whiteUserId) === me || Number(g.blackUserId) === me);
  const finished = !!g.endedAt || FINISHED.has(String(g.status || '').toUpperCase())
    || String(g.status || '').toUpperCase() === 'ABORTED';
  if (mine || staff) {
    return analyze && finished
      ? `/playing/game/${g.id}?analyze=1`
      : `/playing/game/${g.id}`;
  }
  if (g.spectatorAllowed === false || String(g.gameType || '').toUpperCase() === 'VS_COMPUTER') {
    return null;
  }
  return analyze && finished
    ? `/playing/spectate/${g.id}?analyze=1`
    : `/playing/spectate/${g.id}`;
}

function computerPlayAgainHref(g) {
  const st = String(g?.status || '').toUpperCase();
  if (!g?.endedAt && !FINISHED.has(st) && st !== 'ABORTED') return undefined;
  if (String(g?.gameType || '').toUpperCase() !== 'VS_COMPUTER') return undefined;
  const me = Number(getUserId());
  if (!me) return undefined;
  const wasWhite = Number(g.whiteUserId) === me;
  const wasBlack = Number(g.blackUserId) === me;
  if (!wasWhite && !wasBlack) return undefined;
  const color = wasWhite ? 'black' : 'white';
  const level = g.computerLevel || 3;
  const rated = g.rated ? '1' : '0';
  const tc = g.timeControlSeconds || 600;
  const inc = g.incrementMs != null ? Math.round(g.incrementMs / 1000) : 0;
  return `/playing/play?color=${color}&level=${level}&rated=${rated}&tc=${tc}&inc=${inc}`;
}

function pct(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return `${Math.round(Number(n))}%`;
}

function pgnToUci(pgn) {
  if (!pgn || typeof pgn !== 'string') return [];
  try {
    const c = new Chess();
    // Stored PGN may be SAN-only move text without headers
    const text = pgn.includes('[') ? pgn : `[Event "?"]\n\n${pgn}`;
    c.loadPgn(text, { strict: false });
    return c.history({ verbose: true }).map((m) => `${m.from}${m.to}${m.promotion || ''}`);
  } catch {
    return [];
  }
}

export default function ChessProfilePage() {
  const { t } = useTranslation();
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [rematchBusyId, setRematchBusyId] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);
  const isSelf = Number(userId) === Number(getUserId());
  const isParentRole = String(getActiveRole() || '').toUpperCase() === 'PARENT'
    || (getRoles() || []).some((r) => String(r).toUpperCase().replace(/^ROLE_/, '') === 'PARENT');

  useEffect(() => {
    setLoading(true);
    setError('');
    setProfile(null);
    getChessPlayerProfile(userId)
      .then((p) => setProfile(p))
      .catch((e) => {
        const status = e?.response?.status;
        const msg = apiErrorMessage(e, '');
        if (status === 404 || status === 400 || /not found/i.test(msg)) {
          setError('NOT_FOUND');
        } else {
          setError(apiErrorMessage(e, t('playing.loadFailed')));
        }
      })
      .finally(() => setLoading(false));
  }, [t, userId, reloadTick]);

  const playerMissing = error === 'NOT_FOUND';

  const ratings = useMemo(() => {
    const rows = Array.isArray(profile?.ratings) ? profile.ratings : [];
    return [...rows].sort(
      (a, b) => SPEED_ORDER.indexOf(a.speedClass) - SPEED_ORDER.indexOf(b.speedClass),
    );
  }, [profile]);

  const ratingSpark = useMemo(() => {
    const games = Array.isArray(profile?.recentGames) ? [...profile.recentGames] : [];
    // recent is newest-first; sparkline oldest → newest
    const chronological = games.reverse();
    const uid = Number(userId);
    const pts = [];
    chronological.forEach((g) => {
      let after = null;
      if (Number(g.whiteUserId) === uid) after = g.whiteRatingAfter;
      else if (Number(g.blackUserId) === uid) after = g.blackRatingAfter;
      if (after != null && !Number.isNaN(Number(after))) pts.push(Number(after));
    });
    return pts;
  }, [profile, userId]);

  const favoriteOpenings = useMemo(() => {
    const games = Array.isArray(profile?.recentGames) ? profile.recentGames : [];
    const counts = new Map();
    games.forEach((g) => {
      const uci = pgnToUci(g.pgn);
      if (!uci.length) return;
      const open = lookupOpening(uci);
      if (!open?.name) return;
      const key = `${open.eco || '?'}|${open.name}`;
      const prev = counts.get(key) || { eco: open.eco, name: open.name, count: 0 };
      prev.count += 1;
      counts.set(key, prev);
    });
    return [...counts.values()].sort((a, b) => b.count - a.count).slice(0, 5);
  }, [profile]);

  return (
    <div className="page-shell-form playing-page max-w-4xl">
      <PageHeaderSection
        eyebrow={t('nav.playingPlatform')}
        title={playerMissing
          ? t('playing.playerNotFound', { defaultValue: 'Player not found' })
          : (profile?.displayName || t('playing.playerProfile', { defaultValue: 'Player profile' }))}
        subtitle={playerMissing
          ? t('playing.playerNotFoundSubtitle', {
            defaultValue: 'This player does not exist or is no longer available.',
          })
          : t('playing.playerProfileSubtitle', {
            defaultValue: 'Ratings by speed and recent games',
          })}
        aside={(
          <div className="flex flex-wrap gap-2">
            {!playerMissing && userId && Number(userId) !== Number(getUserId()) && !isParentRole && (
              <Button
                variant="page-primary"
                onClick={() => navigate(`/playing/challenge?userId=${encodeURIComponent(userId)}&name=${encodeURIComponent(profile?.displayName || '')}`)}
              >
                {t('playing.challengePlayer')}
              </Button>
            )}
            {!playerMissing && isParentRole && userId && Number(userId) !== Number(getUserId()) && (
              <p className="max-w-xs text-right text-xs text-slate-500">
                {t('playing.parentChallengeHint', {
                  defaultValue: 'Challenges are sent from your child’s account, not yours.',
                })}
              </p>
            )}
            <Button variant="page-secondary" onClick={() => navigate('/playing/leaderboard')}>
              {t('playing.leaderboard')}
            </Button>
          </div>
        )}
      />
      {error && !playerMissing && (
        <PlayingBanner tone="danger">
          <p>{error}</p>
          <div className="mt-2">
            <Button variant="page-secondary" onClick={() => setReloadTick((n) => n + 1)}>
              {t('playing.retry', { defaultValue: 'Retry' })}
            </Button>
          </div>
        </PlayingBanner>
      )}
      {loading ? (
        <div className="list-table-card p-4">
          <PlayingSkeleton rows={5} />
        </div>
      ) : playerMissing || (!profile && !error) ? (
        <PlayingEmptyState
          title={t('playing.playerNotFound', { defaultValue: 'Player not found' })}
          action={(
            <Button variant="page-secondary" onClick={() => navigate('/playing/leaderboard')}>
              {t('playing.leaderboard')}
            </Button>
          )}
        />
      ) : profile ? (
        <div className="space-y-5">
          {(profile.recentAccuracyPct != null
            || (profile.vsYouWins != null && !isSelf)) && (
            <div className="flex flex-wrap gap-2">
              {profile.recentAccuracyPct != null && (
                <span className="playing-meta-pill playing-meta-pill--accent">
                  {t('playing.recentAccuracy', {
                    defaultValue: '{{pct}}% recent accuracy',
                    pct: profile.recentAccuracyPct,
                  })}
                  {profile.recentAccuracyGames != null
                    ? ` · ${t('playing.recentAccuracyGames', {
                      defaultValue: '{{n}} games',
                      n: profile.recentAccuracyGames,
                    })}`
                    : ''}
                </span>
              )}
              {!isSelf && profile.vsYouWins != null && (
                <span className="playing-meta-pill">
                  {t('playing.vsYouRecord', {
                    defaultValue: 'vs you {{w}}–{{d}}–{{l}}',
                    w: profile.vsYouWins ?? 0,
                    d: profile.vsYouDraws ?? 0,
                    l: profile.vsYouLosses ?? 0,
                  })}
                </span>
              )}
            </div>
          )}
          <section className="list-table-card overflow-hidden">
            <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-3">
              <h2 className="text-sm font-semibold text-slate-800">
                {t('playing.ratingsBySpeed', { defaultValue: 'Ratings by speed' })}
              </h2>
            </div>
            {ratings.length === 0 ? (
              <PlayingEmptyState
                title={t('playing.noRatingsYet')}
                action={(
                  <div className="flex flex-wrap justify-center gap-2">
                    {isSelf ? (
                      <Button variant="page-primary" onClick={() => navigate('/playing')}>
                        {t('playing.playNow')}
                      </Button>
                    ) : !isParentRole ? (
                      <Button
                        variant="page-primary"
                        onClick={() => navigate(`/playing/challenge?userId=${encodeURIComponent(userId)}&name=${encodeURIComponent(profile?.displayName || '')}`)}
                      >
                        {t('playing.challengePlayer')}
                      </Button>
                    ) : null}
                    <Button variant="page-secondary" onClick={() => navigate('/playing/live')}>
                      {t('playing.liveGames')}
                    </Button>
                  </div>
                )}
              />
            ) : (
              <div className="responsive-table-scroll">
                <table className="min-w-full text-sm">
                  <thead className="bg-white text-slate-500">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">{t('playing.speedClass', { defaultValue: 'Speed' })}</th>
                      <th className="px-4 py-3 text-right font-semibold">{t('playing.rating')}</th>
                      <th className="px-4 py-3 text-right font-semibold">{t('playing.peakRating', { defaultValue: 'Peak' })}</th>
                      <th className="px-4 py-3 text-right font-semibold">{t('playing.games')}</th>
                      <th className="px-4 py-3 text-right font-semibold">{t('playing.recordShort')}</th>
                      {!isSelf && !isParentRole ? <th className="px-4 py-3 text-right font-semibold" /> : null}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {ratings.map((r) => (
                      <tr key={r.speedClass} className="hover:bg-slate-50/80">
                        <td className="px-4 py-3 font-semibold text-slate-900">
                          {t(`playing.speed.${r.speedClass}`, { defaultValue: r.speedClass })}
                        </td>
                        <td className="px-4 py-3 text-right font-bold tabular-nums">{r.rating}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-slate-600">{r.peakRating ?? r.rating}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-slate-600">{r.gamesPlayed ?? 0}</td>
                        <td className="px-4 py-3 text-right text-xs tabular-nums text-slate-500">
                          {pct(r.winPct)} / {pct(r.drawPct)} / {pct(r.lossPct)}
                          <span className="ml-2 text-slate-400">
                            ({r.wins ?? 0}/{r.draws ?? 0}/{r.losses ?? 0})
                          </span>
                        </td>
                        {!isSelf && !isParentRole && userId ? (
                          <td className="px-4 py-3 text-right">
                            <Button
                              variant="page-secondary"
                              onClick={() => navigate(
                                `/playing/challenge?userId=${encodeURIComponent(userId)}`
                                + `&name=${encodeURIComponent(profile?.displayName || '')}`
                                + `&speed=${encodeURIComponent(r.speedClass)}`,
                              )}
                            >
                              {t('playing.challenge', { defaultValue: 'Challenge' })}
                            </Button>
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {ratingSpark.length >= 2 && (
              <div className="border-t border-slate-100 px-5 py-4">
                <ChartPanel
                  title={t('playing.ratingTrend', { defaultValue: 'Recent rating trend' })}
                  subtitle={`${ratingSpark[0]} → ${ratingSpark[ratingSpark.length - 1]}`}
                  height={180}
                  className="border-0 shadow-none p-0"
                >
                  <TrendLine
                    data={ratingSpark.map((value, i) => ({ name: String(i + 1), value }))}
                    series={[{ dataKey: 'value', name: t('playing.yourRating'), color: CHART_COLORS.primary }]}
                    height={160}
                    showLegend={false}
                  />
                </ChartPanel>
              </div>
            )}
          </section>

          {ratings.length > 0 && (
            <div className="mb-4">
              <ChartPanel
                title={t('charts.gameRecord', { defaultValue: 'Game record' })}
                empty={false}
              >
                <CompositionDonut
                  data={[
                    {
                      name: t('playing.wins', { defaultValue: 'Wins' }),
                      value: ratings.reduce((s, r) => s + (r.wins || 0), 0),
                      color: CHART_COLORS.success,
                    },
                    {
                      name: t('playing.draws', { defaultValue: 'Draws' }),
                      value: ratings.reduce((s, r) => s + (r.draws || 0), 0),
                      color: CHART_COLORS.slate,
                    },
                    {
                      name: t('playing.losses', { defaultValue: 'Losses' }),
                      value: ratings.reduce((s, r) => s + (r.losses || 0), 0),
                      color: CHART_COLORS.danger,
                    },
                  ]}
                  chartSize={180}
                />
              </ChartPanel>
            </div>
          )}

          <section className="list-table-card overflow-hidden">
            <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-3">
              <h2 className="text-sm font-semibold text-slate-800">
                {t('playing.favoriteOpenings', { defaultValue: 'Favorite openings' })}
              </h2>
            </div>
            {favoriteOpenings.length > 0 && (
              <div className="px-4 pt-4">
                <CompareBar
                  data={favoriteOpenings.map((o) => ({
                    name: o.eco || o.name?.slice(0, 12) || '?',
                    value: o.count,
                  }))}
                  height={160}
                  layout="horizontal"
                />
              </div>
            )}
            {favoriteOpenings.length === 0 ? (
              <PlayingEmptyState
                title={t('playing.noOpeningsYet', { defaultValue: 'No openings from recent games yet' })}
                action={isSelf ? (
                  <Button variant="page-secondary" onClick={() => navigate('/playing')}>
                    {t('playing.playNow')}
                  </Button>
                ) : undefined}
              />
            ) : (
              <ul className="divide-y divide-slate-100">
                {favoriteOpenings.map((o) => (
                  <li key={`${o.eco}-${o.name}`} className="flex items-center justify-between gap-3 px-5 py-3 text-sm">
                    <div className="min-w-0">
                      <span className="font-mono text-xs text-slate-400">{o.eco}</span>
                      <span className="ml-2 font-semibold text-slate-900">{localizeOpeningName(o.name, t)}</span>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className="tabular-nums text-slate-500">
                        {o.count}×
                      </span>
                      <Button
                        variant="page-secondary"
                        onClick={() => navigate(exploreOpeningPath(o.eco))}
                      >
                        {t('playing.exploreOpening', { defaultValue: 'Explore' })}
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="list-table-card overflow-hidden">
            <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-3">
              <h2 className="text-sm font-semibold text-slate-800">{t('playing.recentGames', { defaultValue: 'Recent games' })}</h2>
            </div>
            {(profile.recentGames || []).length === 0 ? (
              <PlayingEmptyState
                title={t('playing.noGames')}
                action={(
                  <div className="flex flex-wrap justify-center gap-2">
                    {isSelf ? (
                      <>
                        <Button variant="page-primary" onClick={() => navigate('/playing')}>
                          {t('playing.playNow')}
                        </Button>
                        {!isParentRole && (
                          <Button variant="page-secondary" onClick={() => navigate('/playing/challenge')}>
                            {t('playing.challengePlayer')}
                          </Button>
                        )}
                      </>
                    ) : !isParentRole ? (
                      <Button
                        variant="page-primary"
                        onClick={() => navigate(`/playing/challenge?userId=${encodeURIComponent(userId)}&name=${encodeURIComponent(profile?.displayName || '')}`)}
                      >
                        {t('playing.challengePlayer')}
                      </Button>
                    ) : null}
                  </div>
                )}
              />
            ) : (
              <ul className="divide-y divide-slate-100">
                {(profile.recentGames || []).map((g) => (
                  <GameListRow
                    key={g.id}
                    game={g}
                    href={profileGameHref(g) || undefined}
                    actionLabel={t('playing.review')}
                    analyzeHref={
                      g.endedAt || FINISHED.has(String(g.status || '').toUpperCase())
                        ? (profileGameHref(g, { analyze: true }) || undefined)
                        : undefined
                    }
                    playAgainHref={computerPlayAgainHref(g)}
                    onRematch={canRematchProfileGame(g) ? async () => {
                      if (rematchBusyId) return;
                      setRematchBusyId(g.id);
                      try {
                        const ch = await rematchGame(g.id);
                        if (ch?.gameId) navigate(`/playing/game/${ch.gameId}`);
                        else navigate('/playing');
                      } catch {
                        setError(t('playing.rematchFailed'));
                      } finally {
                        setRematchBusyId(null);
                      }
                    } : undefined}
                    rematchBusy={rematchBusyId === g.id}
                  />
                ))}
              </ul>
            )}
          </section>
        </div>
      ) : null}
    </div>
  );
}
